import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card } from "../../components/ui/card";
import {
  Plus, Trash2, ArrowLeft, Save, FileText, Upload, Download,
  Image as ImageIcon, X, ListChecks, FileJson, Award,
} from "lucide-react";
import { toast } from "sonner";
import MarkdownEditor from "../../components/MarkdownEditor";
import ExamQuestionsEditor, { newQuestion } from "../../components/admin/ExamQuestionsEditor";
import TheoryFilesUploader from "../../components/admin/TheoryFilesUploader";
import { questionsFromCsvText, questionsFromJsonText } from "../../lib/examImport";

const newExam = (order) => ({
  exam_id: null, // null = not yet persisted
  title: "",
  description: "",
  passPercentage: 70,
  timeLimitMinutes: 0,
  order,
  questions: [newQuestion()],
  theoryFiles: [],
});

const fmtSize = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const examToPayload = (ex) => ({
  title: ex.title,
  description: ex.description,
  pass_percentage: Number(ex.passPercentage) || 0,
  time_limit_minutes: Number(ex.timeLimitMinutes) || 0,
  order: Number(ex.order) || 1,
  questions: ex.questions,
});

export default function AdminTestEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const canEditPrice =
    currentUser?.role === "superadmin" ||
    currentUser?.role === "admin" ||
    !!(currentUser?.perms || {}).edit_course_price;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [studyHours, setStudyHours] = useState("");
  // Optional: most courses have none, and it only shows where it exists.
  const [practiceHours, setPracticeHours] = useState("");
  // Goes on the back of the diploma. Never shown to the alumno.
  const [syllabusFile, setSyllabusFile] = useState(null);
  const [syllabusBusy, setSyllabusBusy] = useState(false);
  const [price, setPrice] = useState("");
  const [moreInfo, setMoreInfo] = useState("");
  const [area, setArea] = useState("");
  const [exams, setExams] = useState([newExam(1)]);
  const [saving, setSaving] = useState(false);
  const [headerImage, setHeaderImage] = useState(null);
  const [headerBusy, setHeaderBusy] = useState(false);
  const [generalTheoryFile, setGeneralTheoryFile] = useState(null);
  const [generalTheoryBusy, setGeneralTheoryBusy] = useState(false);
  const [examBusy, setExamBusy] = useState(false); // busy state for add/import-exam actions
  const [existingAreas, setExistingAreas] = useState([]);

  useEffect(() => {
    // Fetch unique areas from all existing courses, for the datalist autocomplete
    api.get("/admin/courses").then(({ data }) => {
      const set = new Set();
      (data || []).forEach((c) => {
        if (c.area && c.area.trim()) set.add(c.area.trim());
      });
      setExistingAreas([...set].sort());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/admin/courses/${id}`).then(({ data }) => {
      setTitle(data.title);
      setDescription(data.description || "");
      setStudyHours(data.study_hours ?? "");
      setPracticeHours(data.practice_hours ?? "");
      setPrice(data.price ?? "");
      setMoreInfo(data.more_info || "");
      setArea(data.area || "");
      setHeaderImage(data.header_image || null);
      setGeneralTheoryFile(data.general_theory_file || null);
      setSyllabusFile(data.syllabus_file || null);
      setExams(
        (data.exams || [])
          .slice()
          .sort((a, b) => (a.order || 1) - (b.order || 1))
          .map((e) => ({
            exam_id: e.exam_id,
            title: e.title || "",
            description: e.description || "",
            passPercentage: e.pass_percentage,
            timeLimitMinutes: e.time_limit_minutes || 0,
            order: e.order || 1,
            questions: (e.questions || []).map((q) => ({
              text: q.text,
              type: q.type,
              options: q.options,
              correct: q.correct || [],
              block: q.block || "",
            })),
            theoryFiles: e.theory_files || [],
          }))
      );
    });
  }, [id]);

  const updateExam = (examIdx, patch) => {
    setExams((exs) => exs.map((e, i) => (i === examIdx ? { ...e, ...patch } : e)));
  };

  // ---- Tema (exam) add/remove ----
  const addExamManually = () => {
    const maxOrder = exams.reduce((m, e) => Math.max(m, Number(e.order) || 0), 0);
    setExams((exs) => [...exs, newExam(maxOrder + 1)]);
  };

  const removeExam = async (examIdx) => {
    const ex = exams[examIdx];
    if (!window.confirm(`¿Eliminar el tema "${ex.title || "sin título"}"?`)) return;
    if (ex.exam_id) {
      try {
        await api.delete(`/admin/courses/${id}/exams/${ex.exam_id}`);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar el tema");
        return;
      }
    }
    setExams((exs) => exs.filter((_, i) => i !== examIdx));
    toast.success("Tema eliminado");
  };

  const importNewExamFromFile = async (file) => {
    if (!file) return;
    const maxOrder = exams.reduce((m, e) => Math.max(m, Number(e.order) || 0), 0);
    const placeholderTitle = file.name.replace(/\.(json|csv)$/i, "") || "Tema importado";

    if (!id) {
      // Curso aún no guardado — parseamos el fichero en el navegador y
      // añadimos el tema directamente al estado local, igual que ya hace
      // "Reimportar preguntas" para un tema sin guardar. No hace falta
      // esperar a guardar el curso para poder importar.
      try {
        const text = await file.text();
        const fname = file.name.toLowerCase();
        let questions;
        if (fname.endsWith(".json")) {
          questions = questionsFromJsonText(text);
        } else if (fname.endsWith(".csv")) {
          questions = questionsFromCsvText(text);
        } else {
          toast.error("Formato no soportado (usa JSON o CSV)");
          return;
        }
        if (!questions.length) {
          toast.error("No se encontraron preguntas en el archivo");
          return;
        }
        setExams((exs) => [
          ...exs,
          {
            exam_id: null,
            title: placeholderTitle,
            description: "Tema importado",
            passPercentage: 70,
            timeLimitMinutes: 0,
            order: maxOrder + 1,
            questions,
            theoryFiles: [],
          },
        ]);
        toast.success("Tema importado. Revisa el título/descripción antes de guardar.");
      } catch (err) {
        toast.error(`Archivo inválido: ${err.message}`);
      }
      return;
    }

    setExamBusy(true);
    try {
      const created = await api.post(`/admin/courses/${id}/exams`, {
        title: placeholderTitle,
        description: "Tema importado",
        pass_percentage: 70,
        order: maxOrder + 1,
        questions: [],
      });
      const newExamDoc = created.data.exams[created.data.exams.length - 1];
      const fd = new FormData();
      fd.append("file", file);
      const imported = await api.post(
        `/admin/courses/${id}/exams/${newExamDoc.exam_id}/import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const serverExam = imported.data.exams.find((e) => e.exam_id === newExamDoc.exam_id);
      setExams((exs) => [
        ...exs,
        {
          exam_id: serverExam.exam_id,
          title: serverExam.title,
          description: serverExam.description,
          passPercentage: serverExam.pass_percentage,
          timeLimitMinutes: serverExam.time_limit_minutes || 0,
          order: serverExam.order,
          questions: serverExam.questions.map((q) => ({
            text: q.text, type: q.type, options: q.options, correct: q.correct || [], block: q.block || "",
          })),
          theoryFiles: serverExam.theory_files || [],
        },
      ]);
      toast.success("Tema importado. Revisa el título/descripción antes de guardar.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo importar el tema");
    } finally {
      setExamBusy(false);
    }
  };

  const importQuestionsIntoExam = async (examIdx, file) => {
    const ex = exams[examIdx];
    if (!file) return;

    if (!ex.exam_id) {
      // Tema not persisted yet (e.g. tema 1 of a brand-new course) — parse
      // the file locally and populate the form; no server round-trip needed
      // until the whole course is saved.
      const hasContent = ex.questions.some((q) => q.text.trim());
      if (hasContent && !window.confirm("Esto reemplaza las preguntas actuales de este tema. ¿Continuar?")) return;
      try {
        const text = await file.text();
        const fname = file.name.toLowerCase();
        let questions;
        if (fname.endsWith(".json")) {
          questions = questionsFromJsonText(text);
        } else if (fname.endsWith(".csv")) {
          questions = questionsFromCsvText(text);
        } else {
          toast.error("Formato no soportado (usa JSON o CSV)");
          return;
        }
        if (!questions.length) {
          toast.error("No se encontraron preguntas en el archivo");
          return;
        }
        updateExam(examIdx, { questions });
        toast.success("Preguntas importadas. Revísalas y guarda el curso.");
      } catch (err) {
        toast.error(`Archivo inválido: ${err.message}`);
      }
      return;
    }

    if (!window.confirm("Esto reemplaza todas las preguntas actuales de este tema. ¿Continuar?")) return;
    setExamBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/courses/${id}/exams/${ex.exam_id}/import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const serverExam = data.exams.find((e) => e.exam_id === ex.exam_id);
      updateExam(examIdx, {
        questions: serverExam.questions.map((q) => ({
          text: q.text, type: q.type, options: q.options, correct: q.correct || [], block: q.block || "",
        })),
      });
      toast.success("Preguntas importadas");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo importar");
    } finally {
      setExamBusy(false);
    }
  };

  // ---- Validation + save ----
  const validate = () => {
    if (!title.trim()) {
      toast.error("El título del curso es obligatorio");
      return false;
    }
    if (!id && exams.length === 0) {
      toast.error("Añade al menos un tema");
      return false;
    }
    for (let ei = 0; ei < exams.length; ei++) {
      const ex = exams[ei];
      if (!ex.title.trim()) {
        toast.error(`Tema ${ei + 1}: el título es obligatorio`);
        return false;
      }
      if (!ex.description.trim()) {
        toast.error(`Tema ${ei + 1}: la descripción es obligatoria`);
        return false;
      }
      for (let qi = 0; qi < ex.questions.length; qi++) {
        const q = ex.questions[qi];
        if (!q.text.trim()) {
          toast.error(`Tema ${ei + 1}, pregunta ${qi + 1}: falta el texto`);
          return false;
        }
        if (q.options.some((o) => !o.trim())) {
          toast.error(`Tema ${ei + 1}, pregunta ${qi + 1}: opciones vacías`);
          return false;
        }
        if (q.correct.length === 0) {
          toast.error(`Tema ${ei + 1}, pregunta ${qi + 1}: marca al menos una respuesta correcta`);
          return false;
        }
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const courseMeta = {
        title,
        description,
        kind: "final", // Curso → siempre diploma; los Talleres se crean desde /admin/workshops
        study_hours: studyHours === "" ? null : Number(studyHours),
        practice_hours: practiceHours === "" ? null : Number(practiceHours),
        price: price === "" ? null : Number(price),
        more_info: moreInfo,
        area: area.trim() || null,
      };
      if (!id) {
        await api.post("/admin/courses", { ...courseMeta, exams: exams.map(examToPayload) });
        toast.success("Guardado");
        navigate("/admin/courses");
        return;
      }
      await api.put(`/admin/courses/${id}`, courseMeta);
      const reorderPayload = [];
      for (const ex of exams) {
        if (ex.exam_id) {
          await api.put(`/admin/courses/${id}/exams/${ex.exam_id}`, examToPayload(ex));
          reorderPayload.push({ exam_id: ex.exam_id, order: Number(ex.order) || 1 });
        } else {
          const { data } = await api.post(`/admin/courses/${id}/exams`, examToPayload(ex));
          const created = data.exams[data.exams.length - 1];
          reorderPayload.push({ exam_id: created.exam_id, order: Number(ex.order) || 1 });
        }
      }
      if (reorderPayload.length) {
        await api.post(`/admin/courses/${id}/exams/reorder`, reorderPayload);
      }
      toast.success("Guardado");
      navigate("/admin/courses");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // ---- Per-tema theory PDFs ----
  const refreshExamTheory = async (examId) => {
    const { data } = await api.get(`/admin/courses/${id}`);
    const serverExam = (data.exams || []).find((e) => e.exam_id === examId);
    if (!serverExam) return;
    setExams((exs) => exs.map((e) => (e.exam_id === examId ? { ...e, theoryFiles: serverExam.theory_files || [] } : e)));
  };

  const uploadExamTheory = async (examIdx, file, replaceFileId = null) => {
    const ex = exams[examIdx];
    if (!ex.exam_id) {
      toast.error("Guarda el curso antes de añadir material a este tema");
      return;
    }
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Solo PDF");
      return;
    }
    setExamBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = replaceFileId
        ? `/admin/courses/${id}/exams/${ex.exam_id}/theory/${replaceFileId}`
        : `/admin/courses/${id}/exams/${ex.exam_id}/theory`;
      const method = replaceFileId ? "put" : "post";
      await api[method](url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshExamTheory(ex.exam_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir PDF");
    } finally {
      setExamBusy(false);
    }
  };

  const deleteExamTheory = async (examIdx, fileId) => {
    const ex = exams[examIdx];
    if (!window.confirm("¿Eliminar este PDF? Los estudiantes ya no podrán descargarlo.")) return;
    setExamBusy(true);
    try {
      await api.delete(`/admin/courses/${id}/exams/${ex.exam_id}/theory/${fileId}`);
      toast.success("PDF eliminado");
      await refreshExamTheory(ex.exam_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setExamBusy(false);
    }
  };

  const downloadExamTheory = async (examId, fileId, name) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/courses/${id}/exams/${examId}/theory/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo descargar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "material.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ---- Course-level general PDF ----
  const uploadGeneralTheory = async (file) => {
    if (!id) {
      toast.error("Guarda el curso antes de añadir el PDF general");
      return;
    }
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Solo PDF");
      return;
    }
    setGeneralTheoryBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = generalTheoryFile
        ? `/admin/courses/${id}/general-theory/${generalTheoryFile.file_id}`
        : `/admin/courses/${id}/general-theory`;
      const method = generalTheoryFile ? "put" : "post";
      const { data } = await api[method](url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGeneralTheoryFile(data);
      toast.success("PDF general guardado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir PDF");
    } finally {
      setGeneralTheoryBusy(false);
    }
  };

  const deleteGeneralTheory = async () => {
    if (!generalTheoryFile) return;
    if (!window.confirm("¿Eliminar el PDF general del curso?")) return;
    setGeneralTheoryBusy(true);
    try {
      await api.delete(`/admin/courses/${id}/general-theory/${generalTheoryFile.file_id}`);
      setGeneralTheoryFile(null);
      toast.success("PDF eliminado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setGeneralTheoryBusy(false);
    }
  };

  const downloadGeneralTheory = async () => {
    if (!generalTheoryFile) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/courses/${id}/general-theory/${generalTheoryFile.file_id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo descargar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = generalTheoryFile.original_name || "material.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ---- Header image ----
  const headerPreviewUrl = headerImage
    ? `${API}/public/courses/${id}/header?v=${headerImage.version || headerImage.uploaded_at || ""}`
    : null;

  const uploadHeader = async (file) => {
    if (!id) {
      toast.error("Guarda el curso antes de añadir imagen");
      return;
    }
    if (!file) return;
    const okTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Formato no soportado. Usa PNG, JPG o WEBP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagen máx. 5 MB");
      return;
    }
    setHeaderBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/courses/${id}/header`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeaderImage(data);
      toast.success("Imagen guardada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir imagen");
    } finally {
      setHeaderBusy(false);
    }
  };

  const deleteHeader = async () => {
    if (!window.confirm("¿Eliminar la imagen de cabecera?")) return;
    setHeaderBusy(true);
    try {
      await api.delete(`/admin/courses/${id}/header`);
      setHeaderImage(null);
      toast.success("Imagen eliminada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setHeaderBusy(false);
    }
  };

  const sortedExamIndexes = exams
    .map((e, i) => i)
    .sort((a, b) => (Number(exams[a].order) || 1) - (Number(exams[b].order) || 1));

  const uploadSyllabus = async (file) => {
    setSyllabusBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/courses/${id}/syllabus`, fd);
      setSyllabusFile(data);
      toast.success(
        data.pages > 1
          ? `Temario subido (${data.pages} páginas tras el diploma)`
          : "Temario subido",
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo subir el temario");
    } finally {
      setSyllabusBusy(false);
    }
  };

  const deleteSyllabus = async () => {
    if (!window.confirm("¿Quitar el temario? Los diplomas volverán a tener una sola página.")) return;
    setSyllabusBusy(true);
    try {
      await api.delete(`/admin/courses/${id}/syllabus`);
      setSyllabusFile(null);
      toast.success("Temario eliminado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
    } finally {
      setSyllabusBusy(false);
    }
  };

  const downloadSyllabus = async () => {
    try {
      const res = await fetch(`${API}/admin/courses/${id}/syllabus/file`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("No disponible");
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("No se pudo abrir el temario");
    }
  };

  return (
    <div className="space-y-6" data-testid="test-editor-page">
      <Button variant="ghost" onClick={() => navigate("/admin/courses")} className="text-gray-600">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver
      </Button>

      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-gray-900">
          {id ? "Editar curso" : "Nuevo curso"}
        </h1>
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="save-test">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4">
        <div>
          <Label htmlFor="title">Título del curso</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="test-title-input" />
        </div>
        <div>
          <Label htmlFor="desc">Descripción general</Label>
          <Textarea id="desc" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="area">Área formativa</Label>
          <Input
            id="area"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="p. ej. Sanidad, Restauración, Servicios Sociales…"
            list="area-suggestions"
            data-testid="test-area-input"
          />
          <datalist id="area-suggestions">
            {(existingAreas || []).map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
          <p className="text-xs text-gray-500 mt-1">
            En el catálogo público los cursos se agrupan por área. Deja en blanco para mostrar en &quot;Otros&quot;.
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="w-44">
            <Label htmlFor="sh">Horas lectivas</Label>
            <Input
              id="sh"
              type="number"
              min={0}
              step="0.5"
              value={studyHours}
              onChange={(e) => setStudyHours(e.target.value)}
              placeholder="Ej: 40"
              data-testid="study-hours-input"
            />
            <p className="text-xs text-gray-500 mt-1">Aparecerán en el PDF.</p>
          </div>
          <div className="w-44">
            <Label htmlFor="ph">Horas de prácticas</Label>
            <Input
              id="ph"
              type="number"
              min={0}
              step="0.5"
              value={practiceHours}
              onChange={(e) => setPracticeHours(e.target.value)}
              placeholder="Opcional"
              data-testid="practice-hours-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Si las hay, salen en el diploma y en la home junto a las lectivas.
            </p>
          </div>
          <div className="w-44">
            <Label htmlFor="price">Precio (€)</Label>
            <Input
              id="price"
              type="number"
              min={0}
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Ej: 49,90"
              disabled={!canEditPrice}
              data-testid="price-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              {canEditPrice
                ? "Opcional. Vacío = sin precio."
                : "Solo superadmin o un gestor con el permiso «Editar precio» puede cambiarlo."}
            </p>
          </div>
        </div>
      </Card>

      {/* "Más info" block (shown in the home modal) */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-3" data-testid="more-info-card">
        <div>
          <Label className="text-sm">Más info (catálogo público)</Label>
          <p className="text-xs text-gray-500 mt-1">
            Este texto se muestra en el botón &quot;Más info&quot; del catálogo de la home pública. Admite formato:
            negrita, cursiva, listas, encabezados y enlaces.
          </p>
        </div>
        <MarkdownEditor
          value={moreInfo}
          onChange={setMoreInfo}
          rows={10}
          placeholder="Describe el curso: a quién va dirigido, temario, requisitos, qué incluye…"
          testId="more-info-editor"
        />
      </Card>

      {/* Header image (shown in catalog card + Más info modal) */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4" data-testid="header-image-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-brand" />
              <h2 className="font-display text-lg text-gray-900">Imagen de cabecera</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-md">
              Aparecerá en la tarjeta del catálogo público y en el modal &quot;Más info&quot;. Recomendado:
              proporción 16:9, máx. 5 MB, PNG/JPG/WEBP.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="header-upload"
              type="file"
              accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadHeader(f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!id || headerBusy}
              onClick={() => document.getElementById("header-upload").click()}
              data-testid="upload-header-image"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {headerImage ? "Reemplazar" : "Subir imagen"}
            </Button>
            {headerImage && (
              <Button
                variant="ghost"
                size="sm"
                disabled={headerBusy}
                onClick={deleteHeader}
                title="Eliminar imagen"
                data-testid="delete-header-image"
              >
                <X className="w-3.5 h-3.5 text-red-500" />
              </Button>
            )}
          </div>
        </div>

        {!id ? (
          <p className="text-xs text-gray-500 italic">
            Guarda el curso primero para poder añadir una imagen.
          </p>
        ) : headerImage ? (
          <div
            className="aspect-[16/9] w-full max-w-md rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
            data-testid="header-image-preview"
          >
            <img
              src={headerPreviewUrl}
              alt={title || "Cabecera del curso"}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[16/9] w-full max-w-md rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400 bg-gray-50">
            <div className="text-center px-4">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">Sin imagen. Sube una para destacar el curso en el catálogo.</p>
            </div>
          </div>
        )}
      </Card>

      {/* General theory PDF (course-level, 0..1) */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4" data-testid="general-theory-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand" />
              <h2 className="font-display text-lg text-gray-900">Material general del curso (PDF)</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-md">
              Un único PDF opcional visible para el alumno hasta que apruebe TODOS los temas del curso.
              Máximo 20 MB.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="general-theory-upload"
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadGeneralTheory(f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!id || generalTheoryBusy}
              onClick={() => document.getElementById("general-theory-upload").click()}
              data-testid="upload-general-theory"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {generalTheoryFile ? "Reemplazar" : "Subir PDF"}
            </Button>
            {generalTheoryFile && (
              <>
                <Button variant="ghost" size="sm" onClick={downloadGeneralTheory} title="Descargar">
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={generalTheoryBusy}
                  onClick={deleteGeneralTheory}
                  title="Eliminar"
                  data-testid="delete-general-theory"
                >
                  <X className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </>
            )}
          </div>
        </div>
        {!id ? (
          <p className="text-xs text-gray-500 italic">Guarda el curso primero para poder añadir el PDF general.</p>
        ) : generalTheoryFile ? (
          <p className="text-sm text-gray-700" data-testid="general-theory-name">
            {generalTheoryFile.original_name} · {fmtSize(generalTheoryFile.size_bytes)}
          </p>
        ) : (
          <p className="text-sm text-gray-500">Sin PDF general.</p>
        )}
      </Card>

      {/* Syllabus — separate card on purpose: it is NOT study material and the
          alumno never sees it, it only becomes the back of their diploma. */}
      <Card
        className="p-6 border border-[#e7d5b4] bg-[#fdf8ef] shadow-none rounded-lg space-y-4"
        data-testid="syllabus-card"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-[#8a6f43]" />
              <h2 className="font-display text-lg text-gray-900">Temario para el reverso del diploma</h2>
            </div>
            <p className="text-xs text-gray-600 mt-1 max-w-lg">
              PDF con el temario del curso. Se imprime <strong>detrás del diploma</strong>: al
              descargarlo saldrán las dos páginas, la portada y este temario.
              <strong> El alumno no lo ve en su panel</strong> — no es material de estudio.
              Máximo 20 MB.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="syllabus-upload"
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadSyllabus(f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!id || syllabusBusy}
              onClick={() => document.getElementById("syllabus-upload").click()}
              className="border-[#e7d5b4] text-[#8a6f43] hover:bg-[#fdf2d9]"
              data-testid="upload-syllabus"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {syllabusBusy ? "Subiendo…" : syllabusFile ? "Reemplazar" : "Subir temario"}
            </Button>
            {syllabusFile && (
              <>
                <Button variant="ghost" size="sm" onClick={downloadSyllabus} title="Ver el temario">
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={syllabusBusy}
                  onClick={deleteSyllabus}
                  title="Quitar"
                  data-testid="delete-syllabus"
                >
                  <X className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </>
            )}
          </div>
        </div>
        {!id ? (
          <p className="text-xs text-gray-500 italic">Guarda el curso primero para poder subir el temario.</p>
        ) : syllabusFile ? (
          <p className="text-sm text-gray-700" data-testid="syllabus-name">
            {syllabusFile.original_name} · {fmtSize(syllabusFile.size_bytes)}
            {syllabusFile.pages ? ` · ${syllabusFile.pages} página${syllabusFile.pages === 1 ? "" : "s"}` : ""}
          </p>
        ) : (
          <p className="text-sm text-gray-600">
            Sin temario: el diploma se emite con una sola página.
          </p>
        )}
      </Card>

      {/* Temas */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-gray-900 flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-brand" /> Temas
        </h2>
        <div className="flex gap-2">
          <input
            id="exam-import-input"
            type="file"
            accept=".json,.csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) importNewExamFromFile(f);
              e.target.value = "";
            }}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={examBusy}
            onClick={() => document.getElementById("exam-import-input").click()}
            title="Crea un tema nuevo importando sus preguntas"
            data-testid="import-new-exam"
          >
            <FileJson className="w-4 h-4 mr-2" /> Importar tema (JSON/CSV)
          </Button>
          <Button variant="outline" size="sm" onClick={addExamManually} data-testid="add-exam-manual">
            <Plus className="w-4 h-4 mr-2" /> Añadir tema manualmente
          </Button>
        </div>
      </div>

      <div className="text-xs text-gray-500 leading-relaxed bg-white border border-gray-200 rounded-lg p-4" data-testid="exam-import-format-legend">
        <p className="font-medium text-gray-700 mb-1">Formato de importación de un tema (examen)</p>
        <p>
          <span className="font-mono-data">JSON</span>:{" "}
          <code className="font-mono-data">{`{ questions: [{ text, type: "single"|"multiple"|"truefalse", options: ["..."], correct: [0,1], block: "opcional" }] }`}</code>
        </p>
        <p className="mt-1">
          <span className="font-mono-data">CSV</span> columnas:{" "}
          <code className="font-mono-data">question, type, options (separadas por |), correct (índices separados por coma), block (opcional)</code>
        </p>
        <p className="mt-1">
          El título, la descripción y el % de aprobado del tema no se importan — rellénalos a mano arriba.
        </p>
      </div>

      <div className="space-y-6">
        {sortedExamIndexes.map((examIdx) => {
          const ex = exams[examIdx];
          return (
            <Card key={examIdx} className="p-6 border-2 border-brand/20 shadow-none rounded-lg space-y-4" data-testid={`exam-card-${examIdx}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-end gap-3 flex-wrap flex-1">
                  <div className="w-28">
                    <Label className="text-xs">Nº de tema</Label>
                    <Input
                      type="number"
                      min={1}
                      value={ex.order}
                      onChange={(e) => updateExam(examIdx, { order: e.target.value })}
                      data-testid={`exam-order-${examIdx}`}
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs">Título del tema *</Label>
                    <Input
                      value={ex.title}
                      onChange={(e) => updateExam(examIdx, { title: e.target.value })}
                      data-testid={`exam-title-${examIdx}`}
                    />
                  </div>
                  <div className="w-32">
                    <Label className="text-xs">% para aprobar</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={ex.passPercentage}
                      onChange={(e) => updateExam(examIdx, { passPercentage: e.target.value })}
                      data-testid={`exam-pass-percentage-${examIdx}`}
                    />
                  </div>
                  <div className="w-36">
                    <Label className="text-xs">Tiempo límite (min)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={ex.timeLimitMinutes}
                      onChange={(e) => updateExam(examIdx, { timeLimitMinutes: e.target.value })}
                      placeholder="0 = sin límite"
                      data-testid={`exam-time-limit-${examIdx}`}
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeExam(examIdx)}
                  title="Eliminar tema"
                  data-testid={`remove-exam-${examIdx}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              <div>
                <Label className="text-xs">Descripción del tema *</Label>
                <Textarea
                  value={ex.description}
                  onChange={(e) => updateExam(examIdx, { description: e.target.value })}
                  data-testid={`exam-description-${examIdx}`}
                />
              </div>

              <TheoryFilesUploader
                files={ex.theoryFiles}
                canUpload={!!ex.exam_id}
                busy={examBusy}
                onUpload={(file, replaceFileId) => uploadExamTheory(examIdx, file, replaceFileId)}
                onDelete={(fileId) => deleteExamTheory(examIdx, fileId)}
                onDownload={(fileId, name) => downloadExamTheory(ex.exam_id, fileId, name)}
                idPrefix={`exam-${examIdx}`}
                disabledHint="Guarda el curso primero para gestionar documentación de este tema."
              />

              <div className="flex items-center justify-end">
                <input
                  id={`exam-questions-import-${examIdx}`}
                  type="file"
                  accept=".json,.csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) importQuestionsIntoExam(examIdx, f);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={examBusy}
                  onClick={() => document.getElementById(`exam-questions-import-${examIdx}`).click()}
                  title="Reemplaza las preguntas de este tema (JSON o CSV)"
                  data-testid={`import-questions-${examIdx}`}
                >
                  <FileJson className="w-3.5 h-3.5 mr-1.5" /> {ex.exam_id ? "Reimportar preguntas" : "Importar preguntas"}
                </Button>
              </div>

              <ExamQuestionsEditor
                questions={ex.questions}
                onChange={(questions) => updateExam(examIdx, { questions })}
                idPrefix={String(examIdx)}
              />
            </Card>
          );
        })}
      </div>
    </div>
  );
}
